import { LoaderCircle, ShieldCheck } from "lucide-react";
import { useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import { apiRequest } from "../lib/api";
import type { SessionUser } from "../lib/types";

type QuarantineTicket = { id: string; quarantinedAt?: string | null; quarantineType?: "spam" | "security" | null };

export function canReleaseSecurityQuarantine(user: SessionUser | null, ticket?: QuarantineTicket | null) {
  return Boolean(user?.kind === "staff" && (user.permissions.includes("security.manage") || user.permissions.includes("*"))
    && ticket?.id && ticket.quarantinedAt && ticket.quarantineType === "security");
}

export function QuarantineReleaseButton({ user, ticket, disabled = false, onReleased, onBusyChange }: {
  user: SessionUser | null;
  ticket?: QuarantineTicket | null;
  disabled?: boolean;
  onReleased: () => Promise<void>;
  onBusyChange?: (busy: boolean) => void;
}) {
  const { showToast } = useToast();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [releasedKey, setReleasedKey] = useState<string | null>(null);
  const ticketKey = ticket ? `${ticket.id}:${ticket.quarantinedAt}` : null;
  const allowed = canReleaseSecurityQuarantine(user, ticket);

  async function release() {
    if (!allowed || !ticket || disabled || inFlight.current) return;
    if (!window.confirm("Release this ticket from security quarantine? It will become visible to the customer and return to the normal queue. Held email and push notifications may resume only when eligible; automatic-email suppression remains in effect. Customer and domain blocks will not be removed.")) return;
    inFlight.current = true; setBusy(true); onBusyChange?.(true);
    let released = false;
    try {
      await apiRequest(`/admin/security/quarantine/${encodeURIComponent(ticket.id)}/release`, { method: "POST", body: {} });
      released = true; setReleasedKey(ticketKey);
      showToast("Ticket released from security quarantine. Eligible notifications can resume; automatic-email suppression remains in effect.", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Ticket could not be released.", "error");
    }
    try {
      // Refresh even after a failed/stale request: another reviewer may already
      // have released it, or the action may have succeeded before a network error.
      await onReleased();
    } catch {
      showToast(released ? "Ticket released, but the view could not refresh. Please reload." : "Could not refresh the ticket state. Please reload.", "error");
    } finally {
      inFlight.current = false; setBusy(false); onBusyChange?.(false);
    }
  }

  if (!allowed || ticketKey === releasedKey) return null;
  return <button className="button button--secondary button--small quarantine-release-button" type="button" disabled={disabled || busy}
    onClick={() => void release()}>{busy ? <LoaderCircle className="spin" aria-hidden="true" size={15} /> : <ShieldCheck aria-hidden="true" size={15} />}{busy ? "Releasing…" : "Release ticket"}</button>;
}
